import { useState, useEffect } from 'react';
import { useProfileData } from './useProfileData';
import { supabase } from '@/integrations/supabase/client';

interface ClaimedAPIAthlete {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  sport: string;
  photo_url?: string;
  current_team_name?: string;
  current_team_logo?: string;
  jersey_number?: number;
  position?: string;
  nationality?: string;
  career_stats?: any;
  current_season_stats?: any;
  is_verified: boolean;
  age?: number;
  height?: string;
  weight?: string;
  birth_date?: string;
  birth_place?: string;
  birthplace_city?: string;
  birthplace_state?: string;
  college?: string;
  draft_round?: number;
  draft_year?: number;
  draft_number?: number;
  years_experience?: number;
  espn_bio?: string;
}

interface ClaimedCreator {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  platform: string;
  follower_count: number | null;
  bio: string | null;
  is_verified: boolean | null;
  claim_status: string | null;
  primary_platform?: boolean | null;
}

export const useTalentData = () => {
  const { data, loading, error, refetch } = useProfileData();
  const [claimedAthlete, setClaimedAthlete] = useState<ClaimedAPIAthlete | null>(null);
  const [claimedCreators, setClaimedCreators] = useState<ClaimedCreator[]>([]);
  const [athleteLoading, setAthleteLoading] = useState(false);

  useEffect(() => {
    const fetchClaimedProfiles = async () => {
      if (!data?.id) return;

      setAthleteLoading(true);
      try {
        // Fetch claimed athlete
        const { data: apiAthlete, error: apiError } = await supabase
          .from('api_athletes')
          .select('*')
          .eq('claimed_by_profile_id', data.id)
          .eq('claim_status', 'approved')
          .maybeSingle();

        if (apiError) {
          console.error('Error fetching claimed athlete:', apiError);
        } else {
          setClaimedAthlete(apiAthlete);
        }

        // Fetch ALL approved claimed creators — a talent can link TikTok,
        // Instagram and YouTube simultaneously and the profile page needs to
        // surface every approved platform.
        const { data: creators, error: creatorError } = await supabase
          .from('creators')
          .select('id, username, display_name, avatar_url, platform, follower_count, bio, is_verified, claim_status, primary_platform')
          .eq('claimed_by_profile_id', data.id)
          .eq('claim_status', 'approved')
          .order('primary_platform', { ascending: false }) // primary first
          .order('follower_count', { ascending: false });

        if (creatorError) {
          console.error('Error fetching claimed creators:', creatorError);
          setClaimedCreators([]);
        } else {
          setClaimedCreators(creators || []);
        }
      } catch (err) {
        console.error('Error fetching claimed profiles:', err);
      } finally {
        setAthleteLoading(false);
      }
    };

    fetchClaimedProfiles();
  }, [data?.id]);

  // Kept for backward compatibility with existing callers that only needed
  // one creator (e.g. "has the user claimed *any* creator profile?" checks).
  const claimedCreator = claimedCreators[0] ?? null;

  return {
    athleteData: data,
    claimedAthlete,
    claimedCreator,
    claimedCreators,
    loading: loading || athleteLoading,
    error,
    refetch
  };
};